const safeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const padNumber = (value) => String(value).padStart(2, "0");

export const getAnnualMaskFileKey = (file = {}, index = 0) =>
  safeText(file.storagePath) ||
  safeText(file.id) ||
  `${safeText(file.fileName || file.savedFileName)}:${index}`;

export const isAnnualMaskPdfFile = (file = {}) =>
  /\.pdf$/i.test(safeText(file.fileName || file.savedFileName)) ||
  safeText(file.contentType).toLocaleLowerCase() === "application/pdf";

const asDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getAnnualMaskFileSortTime = (file = {}) => {
  const timestamp = asDate(
    file.queuedAt ||
      file.localImportedAt ||
      file.uploadedAt ||
      file.updatedAt ||
      file.createdAt ||
      file.createAt,
  );
  if (timestamp) return timestamp.getTime();

  const fileName = safeText(file.savedFileName || file.fileName);
  const nameTimestamp = fileName.match(/^(\d{10,13})_/);
  if (nameTimestamp) {
    const value = Number(nameTimestamp[1]);
    return nameTimestamp[1].length === 10 ? value * 1000 : value;
  }

  return 0;
};

export const sortAnnualMaskFilesByNewest = (files = []) =>
  (Array.isArray(files) ? files : [])
    .filter(Boolean)
    .map((file, index) => ({ file, index }))
    .sort((left, right) => {
      const timeDifference =
        getAnnualMaskFileSortTime(right.file) -
        getAnnualMaskFileSortTime(left.file);
      if (timeDifference) return timeDifference;

      return safeText(right.file.fileName || right.file.savedFileName).localeCompare(
        safeText(left.file.fileName || left.file.savedFileName),
      );
    })
    .map(({ file }) => file);

export const getAnnualMaskSleepStudyReportReference = (file = {}) => ({
  fileName:
    safeText(file.fileName || file.savedFileName) || "Sleep-Study-Report.pdf",
  savedFileName: safeText(file.savedFileName),
  storagePath: safeText(file.storagePath),
  url: safeText(file.url),
  contentType: safeText(file.contentType) || "application/pdf",
  uploadedAt: file.uploadedAt || "",
  uploadedBy: safeText(file.uploadedBy),
  sourceDocId: safeText(file.sourceDocId),
  sourceCollection: safeText(file.sourceCollection) || "report",
  source: "patient-sleep-study",
  annualMaskOwned: false,
});

export const getAnnualMaskLocalUsageFile = (
  manuallySelectedFile,
  therapyReport = {},
) =>
  manuallySelectedFile ||
  (therapyReport && therapyReport.localFile) ||
  null;

export const formatAnnualMaskPressureRange = ({
  minPressure = "",
  maxPressure = "",
  pressure = "",
} = {}) => {
  const minimum = safeText(minPressure);
  const maximum = safeText(maxPressure);

  if (minimum && maximum) return `${minimum}-${maximum}`;
  return minimum || maximum || safeText(pressure);
};

export const toAnnualMaskInputDate = (value) => {
  const text = safeText(value);
  if (!text) return "";

  const isoMatch = text.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${padNumber(isoMatch[2])}-${padNumber(isoMatch[3])}`;
  }

  const dayFirstMatch = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})/);
  if (dayFirstMatch) {
    let year = Number(dayFirstMatch[3]);
    if (year > 2400) year -= 543;
    if (year < 100) year += 2000;
    return `${year}-${padNumber(dayFirstMatch[2])}-${padNumber(dayFirstMatch[1])}`;
  }

  const parsedDate = new Date(text);
  if (Number.isNaN(parsedDate.getTime())) return "";

  return `${parsedDate.getFullYear()}-${padNumber(
    parsedDate.getMonth() + 1,
  )}-${padNumber(parsedDate.getDate())}`;
};

const normalizePdfText = (text = "") =>
  String(text || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t\r\n]+/g, " ")
    .trim();

const extractFirstMatch = (text, patterns = []) => {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && match[1]) return safeText(match[1]);
  }

  return "";
};

const extractNumber = (text, patterns = []) => {
  const value = extractFirstMatch(text, patterns);
  const number = value.replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return number ? number[0] : "";
};

const extractStudyDate = (text) => {
  const dateValue = extractFirstMatch(text, [
    /(?:sleep\s*study|study|test)\s*date\s*[:：#-]?\s*([0-9]{1,4}[\/-][0-9]{1,2}[\/-][0-9]{1,4})/i,
    /date\s*of\s*(?:sleep\s*)?study\s*[:：#-]?\s*([0-9]{1,4}[\/-][0-9]{1,2}[\/-][0-9]{1,4})/i,
    /(?:วันที่ตรวจการนอน|วันที่ตรวจ|วันตรวจ)\s*[:：#-]?\s*([0-9]{1,4}[\/-][0-9]{1,2}[\/-][0-9]{1,4})/i,
  ]);

  return toAnnualMaskInputDate(dateValue);
};

export const extractSleepStudyClinicalValues = (rawText = "") => {
  const text = normalizePdfText(rawText);

  return {
    latestSleepStudyDate: extractStudyDate(text),
    age: extractNumber(text, [
      /\bage\b(?:\s*\((?:years?|yrs?)\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
      /อายุ\s*[:：-]?\s*(\d+(?:\.\d+)?)/,
    ]),
    weight: extractNumber(text, [
      /\bweight\b(?:\s*\((?:kg|kgs|kilograms?)\.?\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
      /น้ำหนัก(?:\s*\((?:กก\.?|kg)\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
    ]),
    height: extractNumber(text, [
      /\bheight\b(?:\s*\((?:cm|centimet(?:er|re)s?)\.?\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
      /ส่วนสูง(?:\s*\((?:ซม\.?|cm)\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
    ]),
    bmi: extractNumber(text, [
      /\bb\.?\s*m\.?\s*i\b\.?(?:\s*\(kg\s*\/\s*m[²2]\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
      /\bbody\s+mass\s+index\b(?:\s*\(BMI\))?\s*[:：-]?\s*(\d+(?:\.\d+)?)/i,
      /ดัชนีมวลกาย\s*[:：-]?\s*(\d+(?:\.\d+)?)/,
    ]),
    ahi: extractNumber(text, [
      /(?:average\s*)?ahi(?:\s*(?:index|value))?\D{0,40}(\d+(?:\.\d+)?)/i,
      /apnea[- ]hypopnea\s*index(?:\s*\(ahi\))?\D{0,40}(\d+(?:\.\d+)?)/i,
      /ดัชนี(?:การ)?หยุดหายใจ(?:และ|ร่วมกับ)?หายใจแผ่ว\D{0,40}(\d+(?:\.\d+)?)/i,
    ]),
    minSpo2: extractNumber(text, [
      /(?:minimum|min\.?|lowest|nadir)\s*(?:spo2|spo₂|o2\s*(?:sat(?:uration)?)?|oxygen\s*saturation)\D{0,40}(\d{1,3}(?:\.\d+)?)\s*%?/i,
      /(?:spo2|spo₂|o2\s*(?:sat(?:uration)?)?|oxygen\s*saturation)\s*(?:minimum|min\.?|lowest|nadir)\D{0,40}(\d{1,3}(?:\.\d+)?)\s*%?/i,
      /(?:minimum|min\.?|lowest|nadir)\s*oxygen\D{0,40}(\d{1,3}(?:\.\d+)?)\s*%?/i,
      /(?:ค่าออกซิเจนต่ำสุด|ออกซิเจนต่ำสุด|ค่าต่ำสุด)\D{0,40}(\d{1,3}(?:\.\d+)?)\s*%?/i,
    ]),
  };
};
